/**
 * EPIC284 Task 7 — Open Work from measurement / claim signals only.
 *
 * Open Work must not inherit Current review backlog, "formal review not opened",
 * provisional-episode existence, or quarantined Current-derived populations.
 * Controls Attention (Epic 278) stays a separate count with a distinct label.
 */

import { resolveCustomerCameraScopeLabel } from '../../utils/customerPlaceLabel';
import type { SafetyLocationLookups } from '../../utils/locationDisplay';
import type {
  SiteConditionMeasurementCoverage,
  SiteConditionMeasurementWindow,
  SiteConditionState,
  SiteConditionStateResponse,
} from './siteConditionState';

export type MeasurementOpenWorkKind =
  | 'camera_telemetry_gap'
  | 'unknown_coverage_interval'
  | 'source_stream_stale'
  | 'claim_review_incomplete'
  | 'since_control_comparability_failed'
  | 'control_recheck_unscheduled';

export type MeasurementOpenWorkItem = {
  id: string;
  kind: MeasurementOpenWorkKind;
  conditionId: string;
  label: string;
};

/** Blockers / reason codes that must never become Open Work on Current. */
export const OPEN_WORK_EXCLUDED_REASON_CODES = new Set([
  'no_enumerable_set',
  'legacy_review_population_quarantined',
  'review_boundary_unavailable',
  'formal_review_not_opened',
]);

const OPEN_WORK_LABELS: Record<MeasurementOpenWorkKind, string> = {
  camera_telemetry_gap: 'Camera telemetry gap needs investigation',
  unknown_coverage_interval: 'Unknown coverage interval needs resolution',
  source_stream_stale: 'Source stream is stale',
  claim_review_incomplete: 'Selected baseline or recheck review is incomplete',
  since_control_comparability_failed: 'Since Control comparability failed',
  control_recheck_unscheduled: 'Recorded control has no scheduled recheck',
};

function conditionDisplayLabel(
  condition: SiteConditionState,
  lookups: SafetyLocationLookups = {},
): string {
  const family = String(condition.conditionFamily || '').trim().replace(/[-_]+/g, ' ');
  const familyLabel = family
    ? family.charAt(0).toUpperCase() + family.slice(1)
    : 'Condition';
  const camera = String(condition.canonicalScope?.cameraIds?.[0] || '').trim();
  if (!camera) return familyLabel;
  const place = resolveCustomerCameraScopeLabel(camera, lookups, {
    zoneId: condition.canonicalScope?.zoneIds?.[0],
  });
  return place && place !== '—' ? `${familyLabel} · ${place}` : familyLabel;
}

function formatOpenWorkLabel(
  kind: MeasurementOpenWorkKind,
  condition: SiteConditionState,
  lookups: SafetyLocationLookups = {},
): string {
  return `${OPEN_WORK_LABELS[kind]} — ${conditionDisplayLabel(condition, lookups)}`;
}

function normalizeCodes(codes: Iterable<string | null | undefined>): string[] {
  return Array.from(codes)
    .map((code) => String(code || '').trim().toLowerCase())
    .filter(Boolean);
}

function shortfallHas(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  code: string,
): boolean {
  const entry = coverage?.shortfallReasonBreakdown?.[code]
    || coverage?.shortfallReasonBreakdown?.[code.toUpperCase()];
  if (!entry) return false;
  return entry.shortfallHours == null || entry.shortfallHours > 0;
}

function partitionSeconds(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  key: keyof NonNullable<SiteConditionMeasurementCoverage['coveragePartition']>,
): number {
  const partition = coverage?.coveragePartition;
  if (!partition) return 0;
  const value = partition[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function isLegacyOrNonClaimReview(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  if (!coverage) return true;
  const blocked = normalizeCodes(coverage.blockedBy || []);
  const reasons = normalizeCodes([
    ...(coverage.blockedBy || []),
  ]);
  if (
    blocked.some((code) => OPEN_WORK_EXCLUDED_REASON_CODES.has(code))
    || reasons.some((code) => OPEN_WORK_EXCLUDED_REASON_CODES.has(code))
  ) {
    return true;
  }
  // Quarantine / Current detach nulls review_set_id and pending counts.
  if (coverage.reviewSetId == null && coverage.pendingReviewCount == null) {
    return true;
  }
  return false;
}

/**
 * Claim-purpose incomplete review only. Current-derived / quarantined pending
 * populations must not inflate Open Work (Task 1 + Task 7).
 */
export function isClaimReviewIncomplete(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  if (!coverage || isLegacyOrNonClaimReview(coverage)) return false;
  const pending = coverage.pendingReviewCount;
  if (pending == null || pending <= 0) return false;
  if (!coverage.reviewSetId) return false;
  const state = String(coverage.reviewWindowState || '').trim().toUpperCase();
  if (state && state !== 'OPEN_PENDING') return false;
  const blocked = normalizeCodes(coverage.blockedBy || []);
  if (blocked.some((code) => OPEN_WORK_EXCLUDED_REASON_CODES.has(code))) {
    return false;
  }
  return true;
}

function hasCameraTelemetryGap(
  window: SiteConditionMeasurementWindow,
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  const reasons = normalizeCodes([
    ...window.reasonCodes,
    ...(coverage?.blockedBy || []),
  ]);
  if (reasons.includes('telemetry_missing')) return true;
  if (coverage?.telemetryMissingHours != null && coverage.telemetryMissingHours > 0) {
    return true;
  }
  if (shortfallHas(coverage, 'telemetry_missing')) return true;
  return partitionSeconds(coverage, 'telemetryUnavailableSeconds') > 0;
}

function hasUnknownCoverageInterval(
  window: SiteConditionMeasurementWindow,
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  const reasons = normalizeCodes(window.reasonCodes);
  if (reasons.includes('unknown') || reasons.includes('unknown_analyzable')) {
    return true;
  }
  if (shortfallHas(coverage, 'unknown')) return true;
  if (partitionSeconds(coverage, 'unknownSeconds') > 0) return true;
  const diagnostics = coverage?.coverageDiagnostics || [];
  return diagnostics.some((row) => {
    const code = String(row.code || '').trim().toLowerCase();
    return code === 'unknown' || code === 'unknown_analyzable';
  });
}

function hasSourceStreamStale(
  window: SiteConditionMeasurementWindow,
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  const display = String(window.displayState || '').trim().toLowerCase();
  if (display === 'stale') return true;
  if (coverage?.producerStaleness === true) return true;
  const reasons = normalizeCodes([
    ...window.reasonCodes,
    ...(coverage?.blockedBy || []),
  ]);
  return (
    reasons.includes('source_freshness_policy_failed')
    || reasons.includes('source_stream_stale')
    || reasons.includes('producer_staleness')
  );
}

function conditionScopeKey(condition: SiteConditionState): string {
  const family = String(condition.conditionFamily || '').trim().toLowerCase();
  const zone = String(condition.canonicalScope?.zoneIds?.[0] || '').trim().toLowerCase();
  const camera = String(condition.canonicalScope?.cameraIds?.[0] || '').trim().toLowerCase();
  // Without zone/camera, keep per-condition identity so unrelated rows are not collapsed.
  if (!zone && !camera) {
    return `id:${String(condition.conditionId || '').trim()}`;
  }
  return `${family}|${zone}|${camera}`;
}

function pushItem(
  items: MeasurementOpenWorkItem[],
  seen: Set<string>,
  condition: SiteConditionState,
  kind: MeasurementOpenWorkKind,
  scopeKey: string,
  lookups: SafetyLocationLookups = {},
): void {
  const conditionId = String(condition.conditionId || '').trim();
  if (!conditionId) return;
  const id = `${kind}:${conditionId}`;
  // Unique by (issue_type, condition_id). Also collapse orphan twins that share
  // family/zone/camera so the same unlabeled kind never repeats.
  // Do not dedupe on customer label alone — unresolved camera names can collide
  // across distinct scopes without leaking raw ids.
  const scopeDedup = `${kind}:scope:${scopeKey || conditionId}`;
  const label = formatOpenWorkLabel(kind, condition, lookups);
  if (seen.has(id) || seen.has(scopeDedup)) return;
  seen.add(id);
  seen.add(scopeDedup);
  items.push({
    id,
    kind,
    conditionId,
    label,
  });
}

export function buildMeasurementOpenWorkItems(
  response: SiteConditionStateResponse | null | undefined,
  lookups: SafetyLocationLookups = {},
): MeasurementOpenWorkItem[] {
  if (!response?.conditions?.length) return [];
  const items: MeasurementOpenWorkItem[] = [];
  const seen = new Set<string>();

  for (const condition of response.conditions) {
    collectForCondition(condition, items, seen, lookups);
  }
  return items;
}

function collectForCondition(
  condition: SiteConditionState,
  items: MeasurementOpenWorkItem[],
  seen: Set<string>,
  lookups: SafetyLocationLookups = {},
): void {
  const conditionId = String(condition.conditionId || '').trim();
  if (!conditionId) return;
  const scopeKey = conditionScopeKey(condition);
  const now = condition.operationalMeasurements.now;
  const coverage = now.summary.measurementCoverage || null;

  // Never open work from provisional episode existence alone.
  // Never open work from formal-review-not-opened / no enumerable set.
  const excludedOnly = normalizeCodes([
    ...now.reasonCodes,
    ...(coverage?.blockedBy || []),
  ]).every((code) => OPEN_WORK_EXCLUDED_REASON_CODES.has(code));
  if (
    excludedOnly
    && normalizeCodes([...now.reasonCodes, ...(coverage?.blockedBy || [])]).length > 0
    && !hasCameraTelemetryGap(now, coverage)
    && !hasUnknownCoverageInterval(now, coverage)
    && !hasSourceStreamStale(now, coverage)
    && !isClaimReviewIncomplete(coverage)
  ) {
    // Fall through to since-control checks only.
  } else {
    if (hasCameraTelemetryGap(now, coverage)) {
      pushItem(items, seen, condition, 'camera_telemetry_gap', scopeKey, lookups);
    }
    if (hasUnknownCoverageInterval(now, coverage)) {
      pushItem(items, seen, condition, 'unknown_coverage_interval', scopeKey, lookups);
    }
    if (hasSourceStreamStale(now, coverage)) {
      pushItem(items, seen, condition, 'source_stream_stale', scopeKey, lookups);
    }
    if (isClaimReviewIncomplete(coverage)) {
      pushItem(items, seen, condition, 'claim_review_incomplete', scopeKey, lookups);
    }
  }

  const since = condition.operationalMeasurements.sinceControl;
  const sinceCoverage = since.summary.measurementCoverage || null;
  if (isClaimReviewIncomplete(sinceCoverage)) {
    pushItem(items, seen, condition, 'claim_review_incomplete', scopeKey, lookups);
  }

  const hasAnchor = Boolean(String(since.windowAnchorId || '').trim());
  const bucketCount = since.summary.bucketCount || since.buckets.length || 0;
  if (
    hasAnchor
    && bucketCount > 0
    && String(since.comparisonStatus || '').trim().toLowerCase() === 'comparison_unavailable'
  ) {
    pushItem(items, seen, condition, 'since_control_comparability_failed', scopeKey, lookups);
  }

  // Recorded control, no follow-up measurement yet ⇒ no scheduled recheck signal.
  if (hasAnchor && bucketCount === 0) {
    pushItem(items, seen, condition, 'control_recheck_unscheduled', scopeKey, lookups);
  }
}

export type OpenWorkBandPresentation = {
  /** Customer-facing Open Work summary (distinct Controls vs measurement labels). */
  summary: string;
  measurementCount: number;
  controlsAttentionCount: number;
  /** True when any Current-derived pending backlog was ignored. */
  ignoredLegacyPending: boolean;
};

/**
 * Compose Overview Open Work copy.
 * Safety Cases attention count and measurement open-work count use distinct labels
 * so "6 open items" is never confused with disposition "pending" backlog.
 */
export function presentOpenWorkBand(params: {
  measurementItems: MeasurementOpenWorkItem[];
  controlsAttentionStatus: 'ready' | 'empty' | 'unauthorized' | 'unavailable' | 'loading' | 'missing_site' | null;
  controlsAttentionCount: number;
  /** Optional diagnostic: raw pending counts seen on Current envelopes (must not appear in summary). */
  legacyPendingReviewTotal?: number | null;
}): OpenWorkBandPresentation {
  const measurementCount = params.measurementItems.length;
  const controlsStatus = params.controlsAttentionStatus;
  const controlsCount =
    controlsStatus === 'ready' ? Math.max(0, params.controlsAttentionCount) : 0;
  const ignoredLegacyPending =
    typeof params.legacyPendingReviewTotal === 'number'
    && params.legacyPendingReviewTotal > 0;

  if (controlsStatus === 'unauthorized') {
    return {
      summary: measurementCount > 0
        ? `${formatMeasurementOpenWork(measurementCount)} Safety Cases attention is unauthorized for this site.`
        : 'Open work is unauthorized for this site.',
      measurementCount,
      controlsAttentionCount: 0,
      ignoredLegacyPending,
    };
  }

  if (
    controlsStatus == null
    || controlsStatus === 'unavailable'
    || controlsStatus === 'loading'
    || controlsStatus === 'missing_site'
  ) {
    return {
      summary: measurementCount > 0
        ? formatMeasurementOpenWork(measurementCount)
        : controlsStatus === 'loading'
          ? 'Open work is loading…'
          : 'Open work is unavailable for this site.',
      measurementCount,
      controlsAttentionCount: 0,
      ignoredLegacyPending,
    };
  }

  const parts: string[] = [];
  if (measurementCount > 0) {
    parts.push(formatMeasurementOpenWork(measurementCount));
  }
  if (controlsCount > 0) {
    parts.push(
      controlsCount === 1
        ? '1 Safety Cases attention item needs a decision or follow-up.'
        : `${controlsCount} Safety Cases attention items need a decision or follow-up.`,
    );
  }

  if (parts.length === 0) {
    return {
      summary: 'No measurement issues or Safety Cases attention items need follow-up.',
      measurementCount: 0,
      controlsAttentionCount: 0,
      ignoredLegacyPending,
    };
  }

  return {
    summary: parts.join(' '),
    measurementCount,
    controlsAttentionCount: controlsCount,
    ignoredLegacyPending,
  };
}

function formatMeasurementOpenWork(count: number): string {
  return count === 1
    ? '1 measurement issue needs follow-up.'
    : `${count} measurement issues need follow-up.`;
}

/** Sum pending review counts that are NOT claim-actionable (must not label Open Work). */
export function sumExcludedPendingReviewCount(
  response: SiteConditionStateResponse | null | undefined,
): number {
  if (!response?.conditions?.length) return 0;
  let total = 0;
  for (const condition of response.conditions) {
    const coverage = condition.operationalMeasurements.now.summary.measurementCoverage;
    if (!coverage) continue;
    const pending = coverage.pendingReviewCount;
    if (pending == null || pending <= 0) continue;
    if (isClaimReviewIncomplete(coverage)) continue;
    total += pending;
  }
  return total;
}
